/**
 * Help on every screen: a round button bottom right opens the assistant (how to do things in the
 * app for everyone; questions about the farm's own data on plans with Ask AI), and "Show me
 * around" walks a new person through the main screens. The tour starts by itself the first time
 * someone signs in, and is remembered on their account so it doesn't start again on another phone.
 */
import { go } from "./route.ts";
import { useCallback, useEffect, useState } from "react";
import { api, type Me } from "./api.ts";
import { Chat } from "./screens/ai.tsx";
import { Icon } from "./ui.tsx";

export function HelpButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button className="help-fab" data-tour="help" onClick={onOpen} aria-label="Help and Ask AI" title="Help and Ask AI">
      <Icon name="spark" size={24} />
      <span>Help</span>
    </button>
  );
}

export function HelpPanel({
  accountId,
  staff,
  screen,
  onClose,
  onTour,
}: {
  accountId: string | undefined;
  staff: boolean;
  screen: string;
  onClose: () => void;
  onTour: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const phone = () => window.matchMedia("(max-width: 820px)").matches;
  return (
    <>
      <div className="help-scrim" onClick={onClose} />
      <aside className="help-panel" role="dialog" aria-label="Help">
        <header>
          <h2>
            <Icon name="spark" size={18} /> Help
          </h2>
          <button className="btn ghost small" onClick={onTour}>
            Show me around
          </button>
          <button className="icon-btn" onClick={onClose} aria-label="Close help">
            <Icon name="close" />
          </button>
        </header>
        <p className="muted small" style={{ margin: 0 }}>
          Ask how to do something in the app. The assistant can only look and explain: switching equipment or changing set-up is always done by you.
        </p>
        <Chat accountId={accountId} staff={staff} screen={screen} onTour={onTour} onNavigate={() => phone() && onClose()} />
        {!staff && (
          <div className="toolbar" style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
            <span className="muted small" style={{ flex: 1 }}>Still stuck, something not working, or an idea?</span>
            <button
              className="btn small"
              onClick={() => {
                go({ name: "support", id: "new", screen });
                onClose();
              }}
            >
              Ask FMS
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

// ------------------------------------------------------------------ tour

interface Step {
  /** The `data-tour` of what to point at; missing (or not on screen) shows the card in the middle. */
  target?: string;
  title: string;
  body: string;
}

function steps(me: Me, farmData: boolean): Step[] {
  const role = me.user.role;
  const staff = role === "fms-admin";
  const switches = role === "owner" || role === "manager" || role === "operator" || staff;
  const edits = role === "owner" || role === "manager" || staff;
  const admin = role === "owner" || role === "manager";
  const phone = window.matchMedia("(max-width: 820px)").matches;
  const first = me.user.name.split(" ")[0];
  const list: Array<Step | false> = [
    {
      title: `Welcome to Think-Control${first ? `, ${first}` : ""}`,
      body: "This quick tour shows you where everything is. It takes about a minute, and you can run it again any time from Help.",
    },
    staff && { target: "accounts", title: "Customers", body: "Choose a customer here first. Everything else then shows that farm, as its owner sees it." },
    {
      target: "farm",
      title: "Farm home",
      body: "Your farm at a glance: anything that needs a look, tanks, weather, soil moisture and pumps. Tap anything to open its device.",
    },
    {
      target: "devices",
      title: "Devices",
      body: switches
        ? "Every pump controller and sensor, by site. Open one to see its readings and history, and to switch its outputs: tap On or Off, then tap again to confirm. It only shows Confirmed once the device reports back."
        : "Every pump controller and sensor, by site. Open one to see its readings, a chart and its history.",
    },
    {
      target: "programs",
      title: "Programs",
      body: edits
        ? "Pumping programs and rules. Start, pause or stop a program, switch rules on and off, or make new ones."
        : switches
          ? "Pumping programs and rules. Start, pause or stop a program, and switch rules on and off."
          : "Pumping programs and rules, and what they're doing now.",
    },
    {
      target: "alerts",
      title: "Alerts",
      body: "Anything that needs attention: a device offline, power lost, a safety rule tripped, a tank low. Tick an alert to show you've seen it." + (edits ? " Set up your own under Set up." : ""),
    },
    !phone && { target: "reports", title: "Reports and map", body: "Water used, run hours and energy per day, and charts of any readings. The farm map is just above." },
    !phone && admin && { target: "admin", title: "Admin", body: "Invite people, choose what each person can see and switch, and your farm settings and plan." },
    {
      target: "me",
      title: "Me",
      body: phone
        ? `Turn on notifications for this phone here, and find the map, reports${admin ? " and Admin" : ""}. Tip: add Think-Control to your home screen so it opens like an app (iPhone: Share → Add to Home Screen).`
        : "Your details, notifications on this computer, and sign out.",
    },
    {
      target: "help",
      title: "Help is always here",
      body: farmData
        ? "Tap Help on any screen to ask how to do something, or ask about your farm: “how full are my tanks?”. It can only look, never switch anything."
        : "Tap Help on any screen to ask how to do something in the app, or to run this tour again.",
    },
  ];
  return list.filter((s): s is Step => !!s);
}

/** The visible element for a step, if it's on screen (the side menu on a computer, the bottom bar on a phone). */
function find(target: string | undefined): HTMLElement | undefined {
  if (!target) return undefined;
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return undefined;
}

export function Tour({ me, farmData, onDone }: { me: Me; farmData: boolean; onDone: () => void }) {
  const list = steps(me, farmData);
  const [i, setI] = useState(0);
  const [box, setBox] = useState<DOMRect | undefined>();
  const step = list[Math.min(i, list.length - 1)]!;

  const measure = useCallback(() => {
    const el = find(step.target);
    if (el) el.scrollIntoView({ block: "nearest" });
    setBox(el?.getBoundingClientRect());
  }, [step.target]);
  useEffect(measure, [measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDone();
      if (e.key === "ArrowRight") setI((x) => Math.min(x + 1, list.length - 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(x - 1, 0));
    };
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("keydown", esc);
    };
  }, [measure, onDone, list.length]);

  const last = i >= list.length - 1;
  const pad = 6;
  // Put the card beside the highlighted item where there's room: below or above it on a phone
  // (the bottom bar), to the right of the side menu on a computer.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(340, vw - 32);
  let pos: { left: number; top?: number; bottom?: number } = { left: (vw - cardW) / 2, top: vh / 2 - 110 };
  if (box) {
    if (box.right + 16 + cardW < vw && box.left < vw / 3) pos = { left: box.right + 16, top: Math.max(16, Math.min(box.top - 20, vh - 260)) };
    else if (box.top > vh / 2) pos = { left: Math.max(16, Math.min(box.left + box.width / 2 - cardW / 2, vw - cardW - 16)), bottom: vh - box.top + 14 };
    else pos = { left: Math.max(16, Math.min(box.left + box.width / 2 - cardW / 2, vw - cardW - 16)), top: box.bottom + 14 };
  }
  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Tour of the app">
      {box ? (
        <div className="tour-hole" style={{ left: box.left - pad, top: box.top - pad, width: box.width + pad * 2, height: box.height + pad * 2 }} />
      ) : (
        <div className="tour-dim" />
      )}
      <div className="tour-card card" style={{ ...pos, width: cardW }}>
        <div className="tour-count small muted">
          {i + 1} of {list.length}
        </div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <div className="toolbar">
          {!last && (
            <button className="btn ghost" onClick={onDone}>
              Skip tour
            </button>
          )}
          <span style={{ flex: 1 }} />
          {i > 0 && (
            <button className="btn" onClick={() => setI(i - 1)}>
              Back
            </button>
          )}
          <button className="btn primary" autoFocus onClick={() => (last ? onDone() : setI(i + 1))}>
            {last ? "Done" : i === 0 ? "Show me" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Starts the tour once for someone who hasn't seen it; remembers on their account when it's finished or skipped. */
export function useTour(me: Me) {
  const [on, setOn] = useState(false);
  const [seen, setSeen] = useState(!!me.user.tourSeenAt);
  useEffect(() => {
    if (seen) return;
    let skip = false;
    try {
      skip = sessionStorage.getItem("tc.tour") === "seen";
    } catch {
      // Storage refused: the account flag still stops it repeating.
    }
    if (skip) return;
    const t = setTimeout(() => setOn(true), 800);
    return () => clearTimeout(t);
  }, [seen]);
  const done = useCallback(() => {
    setOn(false);
    if (!seen) {
      setSeen(true);
      try {
        sessionStorage.setItem("tc.tour", "seen");
      } catch {
        // Fine: the account flag is what counts.
      }
      api.tourSeen().catch(() => {});
    }
  }, [seen]);
  return { on, start: () => setOn(true), done };
}
